import CloseableModal from "@/components/CloseableModal"
import { createBasicSelect, Form, IconButton } from "@/components/form"
import { ActionType, getOperatorText, Operator, type Filter, type ObjectSchemaProperty } from "@/models"
import { faSave } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { computed, shallowRef } from "vue"
import { defineComponent, inferProps, Required, type ReactiveComponent } from "vue-utils"
import { getInputComponent, getMarriageClaimProperties } from "../Helpers"

interface Props {
	selectedFilter: Filter | null
	onSaved(filter: Filter): void
	onCancelled(): void
}

const FilterFormStyling = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem 2rem;

	label {
		margin-bottom: 0.75rem;
	}
`

const MarriageKeySelect = createBasicSelect<ObjectSchemaProperty>()
const MarriageOperatorSelect = createBasicSelect<Operator>()

const QuickFilterModal: ReactiveComponent<Props> = (props) => {
	const property = shallowRef<ObjectSchemaProperty | null>(
		props.selectedFilter === null ? null : (props.selectedFilter.Property ?? null)
	)
	const operator = shallowRef<Operator | null>(props.selectedFilter?.Operator ?? null)
	const value = shallowRef<unknown>(props.selectedFilter?.Value ?? null)
	const inputComponent = computed(() => getInputComponent(property.value!.usedSchema, operator.value!))

	function setOperator(newOperator: Operator) {
		operator.value = newOperator
		if (operator.value === Operator.In || operator.value === Operator.NotIn) {
			value.value = value.value === null ? [] : [value.value]
		}
	}

	function renderInput() {
		const InputComponent = inputComponent.value
		return <InputComponent value={value.value} setValue={(newValue) => (value.value = newValue)} />
	}

	return () => (
		<CloseableModal
			title={`${props.selectedFilter ? ActionType.Update : ActionType.Create} Filter`}
			onClose={props.onCancelled}
			style={{ width: "48rem" }}
		>
			<Form
				class="d-flex flex-column gap-4"
				onSubmit={() =>
					props.onSaved({
						Property: property.value!,
						Operator: operator.value!,
						Value: operator.value! === Operator.Exists || operator.value === Operator.NotExists ? null : value.value,
					})
				}
			>
				<div class={FilterFormStyling}>
					<label>
						<Required label="Field" />
						<MarriageKeySelect
							options={getMarriageClaimProperties()}
							value={property.value}
							setValue={(newProperty) => (property.value = newProperty)}
							getText={(property) => property.formatName()}
							getValue={(property) => property.vueKey()}
							onChange={() => {
								operator.value = null
								value.value = null
							}}
							defaultText="Please select a field"
							required
						/>
					</label>
					{property.value !== null && (
						<label>
							Operator
							<MarriageOperatorSelect
								options={property.value.getValidOperators()}
								value={operator.value}
								setValue={setOperator}
								getText={(operator) => getOperatorText(operator)}
								defaultText="Please select an operator"
								required
							/>
						</label>
					)}
					{property.value !== null &&
						operator.value !== null &&
						operator.value !== Operator.Exists &&
						operator.value !== Operator.NotExists &&
						(operator.value === Operator.In || operator.value === Operator.NotIn ? (
							<div class="flex flex-col spacing-2">
								<Required label="Values" />
								{renderInput()}
							</div>
						) : (
							<label>
								<Required label="Value" />
								{renderInput()}
							</label>
						))}
				</div>
				<IconButton
					icon={faSave}
					color={"primary"}
					class="mt-2"
					type="submit"
					disabled={value.value === null && operator.value !== Operator.Exists && operator.value !== Operator.NotExists}
				/>
			</Form>
		</CloseableModal>
	)
}
export default defineComponent(QuickFilterModal, inferProps<Props>())
